/**
 * Éditer le profil de courtier — chunk 9.AE.1.
 *
 * C'est ce qui ferme réellement `D-NEW-WEB-BROKERS-READ-ONLY` : `PATCH
 * /v1/brokers/:id` existait depuis longtemps, aucun écran ne l'appelait. Les
 * validations restent côté serveur — on ne les rejoue pas ici.
 */
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUpdateBrokerProfile, type TeamMember } from "../../../lib/queries/team";

export function EditBrokerProfileDialog({
  membre,
  onClose,
}: {
  readonly membre: TeamMember;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const idTitre = useId();
  const idLicence = useId();
  const idExpiry = useId();

  const [licence, setLicence] = useState("");
  const [expiry, setExpiry] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const maj = useUpdateBrokerProfile();

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    try {
      await maj.mutateAsync({
        id: membre.id,
        // Une chaîne vide efface la valeur ; `undefined` la laisse intacte. La
        // distinction compte : un champ laissé vide par inadvertance ne doit pas
        // effacer un numéro de permis existant.
        ...(licence.trim().length > 0 ? { license_number: licence.trim() } : {}),
        ...(expiry.length > 0 ? { license_expiry: expiry } : {}),
      });
      onClose();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : t("team.broker_dialog.error"));
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={idTitre}
    >
      <form
        onSubmit={soumettre}
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-lg"
      >
        <h2 id={idTitre} className="text-base font-semibold text-slate-900">
          {t("team.broker_dialog.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-600">{membre.full_name ?? membre.email}</p>

        <label htmlFor={idLicence} className="mt-4 block text-sm font-medium text-slate-700">
          {t("team.broker_dialog.license")}
        </label>
        <input
          id={idLicence}
          type="text"
          value={licence}
          onChange={(e) => setLicence(e.target.value)}
          maxLength={60}
          autoComplete="off"
          className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        />

        <label htmlFor={idExpiry} className="mt-4 block text-sm font-medium text-slate-700">
          {t("team.broker_dialog.expiry")}
        </label>
        <input
          id={idExpiry}
          type="date"
          value={expiry}
          onChange={(e) => setExpiry(e.target.value)}
          className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        />
        <p className="mt-1 text-xs text-slate-500">{t("team.broker_dialog.hint")}</p>

        {erreur !== null && (
          <p className="mt-3 text-sm text-red-700" role="alert">
            {erreur}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={maj.isPending}
            className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            {maj.isPending ? t("team.broker_dialog.saving") : t("team.broker_dialog.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
